import { create } from 'zustand';
import {
  filterByLangValue,
  filterByRange,
  sortByCost,
  sortByLangValue,
} from '../features/table/helpers';
import { PaginationType } from '../types/pagination';
import { SortType } from '../types/props';
import { DishCategoryType } from './api/dish-category-api';
import {
  cloneMenu,
  deleteMenu,
  DishMenuType,
  fetchMenu,
  fetchMenuList,
  FetchMenuListRequest,
  menuUpload,
  menuUploadConfirm,
  MenuUploadRequest,
  MenuUploadType,
  postMenu,
  putMenu,
  putMenuOrder,
} from './api/dish-menu-api';
import { DishSectionType } from './api/dish-section-api';
import { useAlertStore } from './use-alert-store';

interface Store extends Partial<PaginationType> {
  isLoading?: boolean;
  menuList?: DishMenuType[];
  fetchMenuList: (params: FetchMenuListRequest) => Promise<DishMenuType[]>;
  fetchMenu: (id: string) => Promise<Partial<DishMenuType>>;
  postMenu: () => Promise<DishMenuType>;
  cloneMenu: (menu: DishMenuType) => Promise<DishMenuType>;
  putMenu: () => Promise<void>;
  deleteMenu: (id: string) => Promise<void>;
  menu?: Partial<DishMenuType>;
  handleUpdateMenu: (dish: Partial<DishMenuType>) => void;
  handleResetMenu: () => void;

  fetchMenuListSections: (params: FetchMenuListRequest) => Promise<void>;
  menuListSections?: Record<string, DishSectionType[]>;

  setMenuListOrder: (sort: string[]) => void;

  uploadedMenu?: MenuUploadType;
  setUploadedMenu: (data?: MenuUploadType) => void;
  menuUpload: (data: MenuUploadRequest) => Promise<void>;
  menuUploadConfirm: () => Promise<void>;
}

export const useDishMenuStore = create<Store>((set, get) => ({
  handleUpdateMenu: (data) => {
    set({ menu: { ...get().menu, ...data } });
  },
  handleResetMenu: () => {
    set({ menu: undefined });
  },
  fetchMenuList: async (params) => {
    set({ isLoading: true });
    try {
      const resp = await fetchMenuList(params);
      let list = [...resp.menus];
      list.sort((a, b) => sortByCost(a.sort_order, b.sort_order, 'desc')); // sorted menu

      if (params.is_on) {
        list = list.filter((item) => (params.is_on === 'true' ? item.is_on : !item.is_on));
      }

      if (params.menuName) {
        list = list.filter((item) => filterByLangValue(item.name, params.menuName));
      }

      if (params.counts) {
        list = list.filter((item) => filterByRange(item.dishes_count, params.counts));
      }

      if (params.tags) {
        const tags: string[] = params.tags!.split(',');
        list = list.filter((item) => tags.every((tag) => item.tag_ids.includes(Number(tag))));
      }

      if (params.description) {
        list = list.filter((item) => filterByLangValue(item.description, params.description));
      }

      if (params.qrs) {
        list = list.filter((item) => filterByRange(item.dishes_count, params.qrs));
      }

      if (params.field === 'is_on') {
        list = list.sort((a, b) =>
          sortByCost(Number(a.is_on), Number(b.is_on), params.dir as SortType),
        );
      }

      if (params.field === 'counts') {
        list = list.sort((a, b) =>
          sortByCost(a.dishes?.length, b.dishes?.length, params.dir as SortType),
        );
      }

      if (params.field === 'qrs') {
        list = list.sort((a, b) =>
          sortByCost(a.qr_ids.length, b.qr_ids.length, params.dir as SortType),
        );
      }

      if (params.field === 'tags') {
        list = list.sort((a, b) =>
          sortByCost(a.tag_ids.length, b.tag_ids.length, params.dir as SortType),
        );
      }

      if (params.field === 'description') {
        list = list.sort((a, b) =>
          sortByLangValue(a.description, b.description, params.dir as SortType),
        );
      }

      if (params.field === 'menuName') {
        list = list.sort((a, b) => sortByLangValue(a.name, b.name, params.dir as SortType));
      }

      set({ menuList: list });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по меню', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
    return get().menuList || [];
  },
  postMenu: async () => {
    set({ isLoading: true });
    try {
      const menu = get().menu;

      if (!menu) {
        throw new Error('Menu is not defined');
      }

      const preparedDates = menu.working_hours?.specs?.map((item) => ({
        ...item,
        date: item.date?.split('T')[0],
      }));

      if (!menu.working_hours?.week?.length) {
        menu.working_hours = {
          week: Array(7).fill('00:00-23:59'),
        };
      }

      const preparedMenu = {
        ...menu,
        is_on: !!menu.is_on,
        working_hours: { ...menu.working_hours, specs: preparedDates },
      };

      const resp = await postMenu(preparedMenu);
      set({ menuList: [resp, ...(get().menuList || [])] });
      useAlertStore.getState().add({ label: 'Меню успешно добавлено', variant: 'success' });
      return resp;
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить данные по меню', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  putMenu: async () => {
    set({ isLoading: true });
    try {
      const menu = get().menu;

      if (!menu) {
        throw new Error('Menu is not defined');
      }
      const preparedDates = menu.working_hours?.specs?.map((item) => ({
        ...item,
        date: item.date?.split('T')[0],
      }));
      const preparedMenu = {
        ...menu,
        working_hours: { ...menu.working_hours, specs: preparedDates },
      };
      const resp = await putMenu(preparedMenu);
      const mapped = get().menuList?.map((item) =>
        item.id === resp.id ? { ...item, ...resp } : item,
      );
      set({ menuList: mapped });
      useAlertStore.getState().add({ label: 'Меню успешно обновлено', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось обновить данные по меню', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  deleteMenu: async (id) => {
    set({ isLoading: true });
    try {
      await deleteMenu(id);
      set({
        menuList: get().menuList?.filter((item) => item.id !== id),
        menu: get().menuList?.find((item) => item.id !== id),
      });
      useAlertStore.getState().add({ label: 'Меню успешно удалено', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось удалить меню', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  fetchMenu: async (id) => {
    set({ isLoading: true });
    try {
      const menu = await fetchMenu(id);

      const sections = menu?.sections || [];
      const categories = menu?.categories || [];
      const dishes = menu?.dishes || [];

      const categoriesWithDishes = categories.map((category) => {
        const updatedCategory: DishCategoryType = { ...category, dishes: [] };

        dishes.forEach((dish) => {
          if (dish.category_id === category.id) {
            updatedCategory.dishes?.push(dish);
          }
        });

        updatedCategory.dishes?.sort((a, b) => sortByCost(a.sort_order, b.sort_order, 'desc')); // sorted dishes
        return updatedCategory;
      });

      const refilledSections: DishSectionType[] = sections.map((item) => {
        const sectionCategories = categoriesWithDishes.filter(
          (category) => category.section_id === item.id,
        );
        sectionCategories.sort((a, b) => sortByCost(a.sort_order, b.sort_order, 'desc')); // sorted categories

        return {
          ...item,
          categories: sectionCategories,
        };
      });

      refilledSections.sort((a, b) => sortByCost(a.sort_order, b.sort_order, 'desc')); // sorted sections

      const filledMenu = { ...menu, sections: refilledSections };

      const updated = [...(get().menuList || [])].map((item) =>
        item.id === menu.id ? filledMenu : item,
      );

      set({ menu: filledMenu, menuList: updated });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по меню', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
    return get().menu!;
  },
  cloneMenu: async (menu) => {
    set({ isLoading: true });
    try {
      if (!menu) {
        throw new Error('Menu is not defined');
      }

      const preparedDates = menu.working_hours?.specs?.map((item) => ({
        ...item,
        date: item.date?.split('T')[0],
      }));

      if (!menu.working_hours?.week?.length) {
        menu.working_hours = {
          week: Array(7).fill('00:00-23:59'),
        };
      }

      const preparedMenu = {
        ...menu,
        is_on: !!menu.is_on,
        working_hours: { ...menu.working_hours, specs: preparedDates },
      };

      const resp = await cloneMenu(preparedMenu);
      set({ menuList: [resp, ...(get().menuList || [])] });
      useAlertStore.getState().add({ label: 'Меню успешно добавлено', variant: 'success' });
      return resp;
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить данные по меню', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  fetchMenuListSections: async (params) => {
    set({ isLoading: true });
    try {
      const menuList = await get().fetchMenuList(params);
      menuList.forEach(async (menu) => {
        const sections = await fetchMenu(String(menu.id)).then((menu) => menu.sections || []);
        set({ menuListSections: { ...get().menuListSections, [menu.id]: sections } });
      });
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  setMenuListOrder: async (order) => {
    set({ isLoading: true });
    try {
      await putMenuOrder(order);

      get().menuList?.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
      useAlertStore.getState().add({ label: 'Порядок меню успешно сохранен', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить порядок меню', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  menuUpload: async (data) => {
    set({ isLoading: true });
    try {
      const response = await menuUpload(data);
      set({ uploadedMenu: response });
    } catch (e) {
      useAlertStore
        .getState()
        .add({ label: 'Не удалось загрузить данные по меню', variant: 'danger' });
      throw new Error();
    } finally {
      set({ isLoading: false });
    }
  },

  menuUploadConfirm: async () => {
    set({ isLoading: true });
    try {
      const token = get().uploadedMenu?.upload_token;
      if (!token) throw new Error('Token is not defined');
      await menuUploadConfirm(token);
      useAlertStore.getState().add({ label: 'Меню успешно загружено', variant: 'success' });
    } catch (e) {
      useAlertStore
        .getState()
        .add({ label: 'Не удалось загрузить данные по меню', variant: 'danger' });
      throw new Error();
    } finally {
      set({ isLoading: false });
    }
  },

  setUploadedMenu: (data) => {
    set({ uploadedMenu: data });
  },
}));
